import * as React from "react";
import { useEffect, useState } from "react";
import cloneDeep from "lodash/cloneDeep";
import moment from "moment/moment";
import { Input, FormGroup, ControlLabel, HelpBlock } from "components/bootstrap";
import { Select, TimeUnitInput } from "components/common";
import { extractDurationAndUnit } from "components/common/TimeUnitInput";
import * as FormsUtils from "util/FormsUtils";
import AnomalyQueryInput from "security-app/components/AnomalyEvents/AnomalyQueryInput";
export const initialLogVolumeConfig = {
  type: "log-volume",
  query: null,
  historical_time_period_ms: 864e5,
  // 24 hours
  historical_statistic: "P95",
  direction: "HIGH",
  threshold_percent: 300,
  minimum_data_ratio: 0.75
};
const HISTORICAL_TIME_UNITS = ["HOURS", "DAYS"];
const STATISTIC_OPTIONS = [
  { value: "AVG", label: "Average" },
  { value: "MEDIAN", label: "Median" },
  { value: "P95", label: "95th Percentile" },
  { value: "P99", label: "99th Percentile" }
];
const DIRECTION_OPTIONS = [
  { value: "HIGH", label: "High (volume spike)" },
  { value: "LOW", label: "Low (volume drop)" }
];
const LogVolumeEventForm = ({ eventDefinition, onChange, validation: _validation, section = "config" }) => {
  var _a, _b, _c, _d, _e;
  const [currentConfig, setCurrentConfig] = useState(() => {
    var _a2;
    return {
      ...eventDefinition.config,
      config: (_a2 = eventDefinition.config.config) != null ? _a2 : initialLogVolumeConfig
    };
  });
  const subtypeConfig = (_a = currentConfig.config) != null ? _a : initialLogVolumeConfig;
  const historicalPeriod = extractDurationAndUnit(subtypeConfig.historical_time_period_ms, HISTORICAL_TIME_UNITS);
  const [historicalDuration, setHistoricalDuration] = useState(historicalPeriod.duration);
  const [historicalUnit, setHistoricalUnit] = useState(historicalPeriod.unit);
  const setCurrentConfigAndPropagateChange = (config) => {
    setCurrentConfig(config);
    onChange("config", config);
  };
  const updateSubtypeConfig = (name, value) => {
    var _a2;
    const config = cloneDeep(eventDefinition.config);
    const nextSubtypeConfig = cloneDeep((_a2 = config.config) != null ? _a2 : subtypeConfig);
    nextSubtypeConfig[name] = value;
    config.config = nextSubtypeConfig;
    setCurrentConfigAndPropagateChange(config);
  };
  const handleSubtypeInputChange = (event) => {
    const { name } = event.target;
    const value = FormsUtils.getValueFromInput(event.target);
    updateSubtypeConfig(name, value);
  };
  const handleHistoricalPeriodChange = (nextValue, nextUnit) => {
    const durationInMs = moment.duration(Math.max(nextValue, 1), nextUnit.toLowerCase()).asMilliseconds();
    setHistoricalDuration(nextValue);
    setHistoricalUnit(nextUnit);
    updateSubtypeConfig("historical_time_period_ms", durationInMs);
  };
  const handleStatisticChange = (nextValue) => {
    updateSubtypeConfig("historical_statistic", nextValue != null ? nextValue : "P95");
  };
  const handleDirectionChange = (nextValue) => {
    updateSubtypeConfig("direction", nextValue != null ? nextValue : "HIGH");
  };
  const handleQueryChange = (changeEvent) => {
    updateSubtypeConfig("query", changeEvent.target.value);
    return Promise.resolve(changeEvent.target.value);
  };
  useEffect(() => {
    const { execute_every_ms: executeEveryMs, search_within_ms: searchWithinMs } = eventDefinition.config;
    if (executeEveryMs > 0 && searchWithinMs !== executeEveryMs) {
      const config = cloneDeep(eventDefinition.config);
      config.search_within_ms = executeEveryMs;
      onChange("config", config);
    }
  }, [eventDefinition.config, onChange]);
  const thresholdHelp = subtypeConfig.direction === "HIGH" ? "Alert when current volume exceeds the baseline by this percentage (e.g. 300 means 3x the baseline)." : "Alert when current volume drops below this percentage of the baseline (e.g. 50 means half the baseline).";
  if (section === "query") {
    return /* @__PURE__ */ React.createElement(
      AnomalyQueryInput,
      {
        controlId: "log-volume-query",
        value: (_b = subtypeConfig.query) != null ? _b : "",
        onChange: handleQueryChange,
        streams: (_c = eventDefinition.config.streams) != null ? _c : [],
        eventDefinition
      }
    );
  }
  if (section !== "config") {
    return null;
  }
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(FormGroup, { controlId: "log-volume-historical-period" }, /* @__PURE__ */ React.createElement(
    TimeUnitInput,
    {
      label: "Historical Time Period",
      update: handleHistoricalPeriodChange,
      value: historicalDuration,
      unit: historicalUnit,
      units: HISTORICAL_TIME_UNITS,
      clearable: true,
      required: true
    }
  ), /* @__PURE__ */ React.createElement(HelpBlock, null, "How far back to look when computing the historical baseline.")), /* @__PURE__ */ React.createElement(FormGroup, { controlId: "log-volume-statistic" }, /* @__PURE__ */ React.createElement(ControlLabel, null, "Baseline Statistic"), /* @__PURE__ */ React.createElement(
    Select,
    {
      id: "log-volume-statistic-select",
      name: "historical_statistic",
      options: STATISTIC_OPTIONS,
      value: subtypeConfig.historical_statistic,
      clearable: false,
      menuPlacement: "bottom",
      onChange: handleStatisticChange
    }
  ), /* @__PURE__ */ React.createElement(HelpBlock, null, "Statistic used to summarize the historical bucket counts into a single baseline value.")), /* @__PURE__ */ React.createElement(FormGroup, { controlId: "log-volume-direction" }, /* @__PURE__ */ React.createElement(ControlLabel, null, "Alert Direction"), /* @__PURE__ */ React.createElement(
    Select,
    {
      id: "log-volume-direction-select",
      name: "direction",
      options: DIRECTION_OPTIONS,
      value: subtypeConfig.direction,
      clearable: false,
      menuPlacement: "bottom",
      onChange: handleDirectionChange
    }
  ), /* @__PURE__ */ React.createElement(HelpBlock, null, "Whether to alert on volume spikes or drops.")), /* @__PURE__ */ React.createElement(
    Input,
    {
      id: "log-volume-threshold",
      name: "threshold_percent",
      label: "Threshold Percent",
      type: "number",
      min: 1,
      step: 1,
      value: (_d = subtypeConfig.threshold_percent) != null ? _d : "",
      onChange: handleSubtypeInputChange,
      help: thresholdHelp,
      required: true
    }
  ), /* @__PURE__ */ React.createElement(
    Input,
    {
      id: "log-volume-min-data-ratio",
      name: "minimum_data_ratio",
      label: "Minimum Data Ratio",
      type: "number",
      min: 0,
      max: 1,
      step: 0.01,
      value: (_e = subtypeConfig.minimum_data_ratio) != null ? _e : "",
      onChange: handleSubtypeInputChange,
      help: "Minimum fraction of historical buckets that must contain data before alerting (0-1).",
      required: true
    }
  ), /* @__PURE__ */ React.createElement(HelpBlock, null, 'The check interval (bucket size) matches the "Execute search every" interval configured above.'));
};
export default LogVolumeEventForm;
